import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import { getVisitorId, getSessionId, getStoredUtm } from '../tracker';
import { useCmsPage, c } from '../lib/cms';

const TIME_SLOTS = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'];

function getNextWeekdays(count) {
  const days = [];
  let d = dayjs().add(1, 'day');
  while (days.length < count) {
    if (d.day() !== 0 && d.day() !== 6) days.push(d);
    d = d.add(1, 'day');
  }
  return days;
}

export default function DemoPage() {
  const cmsPage = useCmsPage('xentrios', 'demo');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'See Xentrios in action.');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', "30-minute personalized walkthrough. We'll show you exactly how Xentrios handles your workflows, your integrations, and your reporting — using your data if you'd like.");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', locations: '', pms: '', message: '',
    preferredDate: '', preferredTime: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const dates = getNextWeekdays(10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Post to Xentrios server.js /api/demo which handles Slack + pipeline
      await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form: { ...form, serviceInterest: 'xentrios' },
          page: '/xentrios/demo',
          site: 'xentrios',
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          utm: getStoredUtm()
        })
      });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div>
        <Helmet><title>Demo Requested — Xentrios</title></Helmet>
        <section className="xen-hero" style={{ padding: '6rem 0' }}>
          <div className="xen-container" style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</p>
            <h1>You're on the calendar.</h1>
            <p className="subtitle">
              We'll send a confirmation to {form.email} with meeting details.
            </p>
            {form.preferredDate && (
              <p style={{ color: 'var(--xen-accent-light)', fontSize: '1.1rem', marginTop: '1rem' }}>
                Requested: {form.preferredDate} at {form.preferredTime || 'TBD'} ET
              </p>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Helmet><title>Request a Demo — Xentrios</title></Helmet>

      <section className="xen-hero" style={{ padding: '4rem 0 3rem' }}>
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Get Started</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
        </div>
      </section>

      <section className="xen-section">
        <div className="xen-container">
          <div className="xen-form">
            {/* Step indicator */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center' }}>
              <div style={{ padding: '0.4rem 1rem', borderRadius: 20, background: step === 1 ? 'var(--xen-accent)' : '#e8e9f0', color: step === 1 ? '#fff' : '#999', fontWeight: 600, fontSize: '0.85rem' }}>1. About You</div>
              <div style={{ padding: '0.4rem 1rem', borderRadius: 20, background: step === 2 ? 'var(--xen-accent)' : '#e8e9f0', color: step === 2 ? '#fff' : '#999', fontWeight: 600, fontSize: '0.85rem' }}>2. Pick a Time</div>
            </div>

            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <>
                  <input type="text" placeholder="Full Name *" value={form.name} onChange={set('name')} required />
                  <input type="email" placeholder="Work Email *" value={form.email} onChange={set('email')} required />
                  <input type="text" placeholder="Organization Name *" value={form.company} onChange={set('company')} required />
                  <input type="tel" placeholder="Phone" value={form.phone} onChange={set('phone')} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <input type="text" placeholder="Number of Locations" value={form.locations} onChange={set('locations')} />
                    <select value={form.pms} onChange={set('pms')} style={{ color: form.pms ? '#333' : '#999' }}>
                      <option value="">Current PMS</option>
                      <option value="dentrix">Dentrix</option>
                      <option value="dentrix_enterprise">Dentrix Enterprise</option>
                      <option value="eaglesoft">Eaglesoft</option>
                      <option value="open_dental">Open Dental</option>
                      <option value="curve">Curve Dental</option>
                      <option value="cloud9">Cloud 9</option>
                      <option value="other">Other</option>
                      <option value="none">Starting Fresh</option>
                    </select>
                  </div>
                  <textarea placeholder="Anything specific you'd like to see?" value={form.message} onChange={set('message')} rows={3} />
                  <button type="button" className="xen-btn xen-btn-primary"
                    style={{ width: '100%', padding: '0.85rem', fontSize: '1rem', marginTop: '0.5rem' }}
                    onClick={() => { if (form.name && form.email && form.company) setStep(2); }}
                    disabled={!form.name || !form.email || !form.company}>
                    Next — Pick a Time
                  </button>
                </>
              )}

              {step === 2 && (
                <>
                  <p style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Select a day:</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {dates.map(d => {
                      const label = d.format('ddd, MMM D');
                      const val = d.format('YYYY-MM-DD');
                      const selected = form.preferredDate === val;
                      return (
                        <button key={val} type="button" onClick={() => setForm(f => ({ ...f, preferredDate: val }))}
                          style={{
                            padding: '0.6rem 0.25rem', borderRadius: 8, fontSize: '0.75rem', fontWeight: 600,
                            border: selected ? '2px solid var(--xen-accent)' : '1px solid #ddd',
                            background: selected ? 'var(--xen-accent)' : '#fff',
                            color: selected ? '#fff' : '#333',
                            cursor: 'pointer', fontFamily: 'inherit'
                          }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <p style={{ fontWeight: 700, marginBottom: '0.75rem' }}>Select a time (ET):</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {TIME_SLOTS.map(t => {
                      const selected = form.preferredTime === t;
                      return (
                        <button key={t} type="button" onClick={() => setForm(f => ({ ...f, preferredTime: t }))}
                          style={{
                            padding: '0.6rem', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600,
                            border: selected ? '2px solid var(--xen-accent)' : '1px solid #ddd',
                            background: selected ? 'var(--xen-accent)' : '#fff',
                            color: selected ? '#fff' : '#333',
                            cursor: 'pointer', fontFamily: 'inherit'
                          }}>
                          {t}
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button type="button" className="xen-btn xen-btn-outline" onClick={() => setStep(1)}
                      style={{ flex: 1, padding: '0.85rem' }}>
                      Back
                    </button>
                    <button type="submit" className="xen-btn xen-btn-primary" disabled={submitting || !form.preferredDate || !form.preferredTime}
                      style={{ flex: 2, padding: '0.85rem', fontSize: '1rem' }}>
                      {submitting ? 'Submitting...' : 'Confirm Demo Request'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      <section className="xen-section xen-section-light xen-section-center">
        <div className="xen-container">
          <h3>What to expect</h3>
          <div className="xen-features" style={{ marginTop: '1.5rem' }}>
            <div className="xen-feature">
              <h3>1. Quick conversation</h3>
              <p>We'll ask a few questions about your practice, current PMS, and what's not working. No generic sales pitch.</p>
            </div>
            <div className="xen-feature">
              <h3>2. Live walkthrough</h3>
              <p>30-minute demo tailored to your specific workflows — scheduling, billing, clinical, or all of it.</p>
            </div>
            <div className="xen-feature">
              <h3>3. Custom quote</h3>
              <p>If it's a fit, we'll put together pricing based on your practice size and configuration. No pressure.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
